import { ReactNode } from 'react';
import { Stack, SxProps } from '@mui/material';
import { SystemCssProperties } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { useRef } from 'react';

import { Weekday } from '@occc/graphql';

import { CalendarGridTimeColumn } from '../calendar-grid-time-column/calendar-grid-time-column.component';
import {
  CALENDAR_GRID_DAYS_START_FROM_MONDAY,
  CALENDAR_GRID_DAYS_START_FROM_SUNDAY,
  CALENDAR_GRID_DEFAULTS,
} from '../../constants';
import { CalendarGridDayColumn } from '../calendar-grid-day-column/calendar-grid-day-column.component';
import {
  CalendarGridDayOverlappedTimeLineItem,
  CalendarGridDayTimeLineItem,
  CalendarGridTimeMode,
  OnOverlappedTimeLineItemProps,
} from '../../types';
import { normaliseSx } from '../../utils';

export type CalendarGridWeekProps = {
  firstDay?: Weekday.Sunday | Weekday.Monday;
  timeMode?: CalendarGridTimeMode;
  scheduler?: Partial<Record<Weekday, CalendarGridDayTimeLineItem[]>>;
  gridWidth?: number;
  onClick?: (item: CalendarGridDayOverlappedTimeLineItem) => void;
  onOverlappedProps?: OnOverlappedTimeLineItemProps;
  renderAction?: (item: CalendarGridDayTimeLineItem, textColor?: string) => ReactNode;
  sx?: SxProps;
};

export const CalendarGridWeek = ({
  firstDay = Weekday.Sunday,
  timeMode,
  gridWidth = CALENDAR_GRID_DEFAULTS.gridWidth,
  scheduler,
  onClick,
  onOverlappedProps,
  renderAction,
  sx,
}: CalendarGridWeekProps) => {
  const workingAreaRef = useRef<HTMLDivElement>(null);

  const source =
    firstDay === Weekday.Monday
      ? CALENDAR_GRID_DAYS_START_FROM_MONDAY
      : CALENDAR_GRID_DAYS_START_FROM_SUNDAY;

  return (
    <Stack sx={[containerStyle, ...normaliseSx(sx)]} ref={workingAreaRef}>
      <CalendarGridTimeColumn
        mode={timeMode}
        gridWidth={gridWidth}
        borderRight={gridWidth}
        renderHeader={() => null}
      />
      <Stack flex={1} sx={[contentStyle]}>
        {source.map((day, index) => (
          <CalendarGridDayColumn
            key={day}
            day={day}
            gridWidth={gridWidth}
            tinted={index % 2 === 0}
            items={scheduler?.[day]}
            onClick={onClick}
            onOverlappedProps={onOverlappedProps}
            renderAction={renderAction}
            workingAreaRef={workingAreaRef}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const containerStyle: SystemCssProperties = {
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flexWrap: 'nowrap',
};

const contentStyle: SystemCssProperties = {
  ...containerStyle,
  overflowX: 'scroll',
};
